<template>
  <div
    class="SiteNotice transition-all duration-500 w-full type-xs relative z-[1002]"
    :class="{
      'h-32 desk:h-40': shouldShowNotice,
      'h-0': !shouldShowNotice,
    }"
    :style="bgAndTextColor"
  >
    <div class="desk:layout-container  items-end justify-between h-full w-full relative">
      <div
        class="mobOnly:hidden absolute top-0 left-0 h-full bg-bg-base z-[2] pr-32"
        style="background: linear-gradient(90deg, var(--siteNoticeBg) 0%, var(--siteNoticeBg) 85%, transparent 100%);"
      >
        <ConsumerType :invert-text-color="effectiveInvertText" />
      </div>

      <!-- * * * * Site message -->
      <div v-if="settings.displaySiteMessage && !hideSiteMessage" class="basis-full h-full">
        <div
          v-if="siteMessageText !== false"
          class="px-16 flex items-center justify-center h-full py-[10px]"
        >
          <div class="basis-full relative flex justify-center items-center">
            <div
              ref="wysiwygContent"
              class="wysiwyg truncate-p"
              :class="{ 'text-text-inverse': settings.invertTextColorMessage,}"
              v-html="siteMessageText"
            />
            <button
              v-if="settings.displayReadMoreContent"
              class="text-10 desk:text-12 whitespace-nowrap leading-single px-6 underline"
              :class="`color-${settings.siteMessageBackgroundColor}`"
              type="button"
              @click="openReadMoreModal"
            >
              {{ $t('productPage.truncateTextCtaClosed') }}
            </button>

            <div
              v-if="settings.displayDismissSiteMessage"
              class="mobOnly:hidden basis-[16px] h-full flex justify-center items-center shrink-0 desk:cursor-pointer"
            >
              <button type="button" @click="closeSiteMessage">
                <img
                  alt=""
                  :src="settings.invertTextColorMessage ? '/icons/close-inv.svg' : '/icons/close.svg'"
                  class="h-[16px] w-[16px]"
                >
              </button>
            </div>
          </div>

          <div
            v-if="settings.displayDismissSiteMessage"
            class="desk:hidden basis-[16px] h-full flex justify-center items-center shrink-0 desk:cursor-pointer"
          >
            <button type="button" @click="closeSiteMessage">
              <img
                alt=""
                :src="settings.invertTextColorMessage ? '/icons/close-inv.svg' : '/icons/close.svg'"
                class="h-[16px] w-[16px]"
              >
            </button>
          </div>
        </div>
      </div>

      <!-- * * * * Notifications for order proposal -->
      <div
        v-else-if="hasNotification"
        class="basis-full relative flex items-center justify-center h-full w-full type-xs  bg-bg-secondary"
      >
        <div class="flex gap-4">
          {{ $t('mypage.notifications.orderProposal.text') }}!
          <nuxt-link
            class="underline cursor-pointer"
            :to="accountUrl('orderProposals')"
            @click="e => trackButtonClick(e, 'banner_link_click')"
          >
            {{ $t('mypage.notifications.orderProposal.btn') }}
          </nuxt-link>
        </div>
        <div class="absolute top-8 right-8 desk:right-24">
          <button
            type="button"
            @click="userStore.markAllNotificationAsSeen('orderProposal')"
          >
            <img
              src="/icons/close.svg"
              class="h-16 w-16 inline-block cursor-pointer"
              alt="Close"
            >
          </button>
        </div>
      </div>

      <!-- * * * * Marquee -->
      <div
        v-else-if="settings.displaySiteMarquee && settings.siteMarqueeItemList.length > 0"
        class="basis-full h-full flex items-center"
      >
        <client-only>
          <Vue3Marquee
            :clone="true"
            :duration="duration"
            class="w-full select-none"
          >
            <div class="my-2 flex items-center">
              <div
                v-for="item in settings.siteMarqueeItemList"
                :key="item._uid"
                :class="settings.invertTextColorMarquee ? 'text-lightest' : 'text-darkest'"
                class="whitespace-nowrap transition mr-16 desk:mr-24 type-tagline-xxs font-semibold odd:font-light"
              >
                {{ item.text }}
              </div>
            </div>
          </Vue3Marquee>
        </client-only>
      </div>

      <!-- * * * * Free delivery days for logged in users -->
      <div
        v-else-if="showFreeDeliveryDays"
        :style="{backgroundColor : settings.siteUspBackgroundColor?.color}"
        class="basis-full flex justify-center items-center h-full w-full"
      >
        <div
          class="flex items-center gap-8"
          :class="settings.siteUspTextColor ? 'text-lightest' : 'text-darkest'"
        >
          <img
            src="/icons/truck.svg"
            class="w-16 h-16"
            :class="{ 'invert': settings.siteUspTextColor }"
            alt=""
          >
          <span class="type-headline-xs">{{ $t('siteNotice.freeDeliveryDays') }}:</span>
          <template v-for="(day, index) in freeDeliveryDaysList" :key="index">
            <div class="w-1 h-16 bg-border-base" />
            <span>{{ day }}</span>
          </template>
        </div>
      </div>

      <!-- * * * * Site usps -->
      <div
        v-else-if="hasSiteUsps"
        :style="{backgroundColor : settings.siteUspBackgroundColor?.color}"
        class="basis-full flex justify-center items-center h-full w-full"
      >
        <div
          class="relative flex items-center desk:hidden w-full h-full"
          :class="settings.siteUspTextColor ? 'text-lightest' : 'text-darkest'"
        >
          <div
            v-for="(usp, index) in settings.siteUspList"
            :key="usp._uid"
            class="absolute w-full flex flex-row items-center justify-center gap-8 usp-text"
            :class="{ active: index === currentUspIndex }"
          >
            <img
              :src="settings.siteUspTextColor ? '/icons/check-circle-inv.svg' : '/icons/check-circle.svg'"
              class="w-16 h-16 text-14"
              alt="✔"
            >
            <div>{{ usp.text }}</div>
          </div>
        </div>
        <div
          class="hidden desk:flex items-center gap-16"
          :class="settings.siteUspTextColor ? 'text-lightest' : 'text-darkest'"
        >
          <div
            v-for="usp in settings.siteUspList"
            :key="usp._uid"
            class="shrink-0 flex gap-8"
          >
            <img
              :src="settings.siteUspTextColor ? '/icons/check-circle-inv.svg' : '/icons/check-circle.svg'"
              class="w-16 h-16 text-14"
              alt="✔"
            >
            <div>{{ usp.text }}</div>
          </div>
        </div>
      </div>

      <div
        class="mobOnly:hidden absolute top-0 right-0 text-right h-full pl-32 z-[2]"
        style="background: linear-gradient(90deg, transparent 0%, var(--siteNoticeBg) 15%, var(--siteNoticeBg) 100%);"
      >
        <div class="flex items-center h-full gap-12">
          <template v-if="globalContentStore.getNewB2CDesign">
            <LocationSelector :invert-text-color="effectiveInvertText" />
            <div class="w-1 h-16 bg-border-base" />
          </template>
          <LangCurrencySelector :invert-text-color="effectiveInvertText" />
        </div>
      </div>
    </div>

    <Teleport to="body">
      <transition name="fromRight">
        <GenericSideMenu
          v-if="showSiteNotice"
          position="right"
          @close="uiStore.closeAllModal()"
        >
          <div>
            <div class="wysiwyg" v-html="readMoreContent" />
          </div>
        </GenericSideMenu>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { StoryBlokSiteNoticeData } from '#constants/types/storyblok';
import { useGlobalContentStore } from '~/store/globalContent';
import type { ISbRichtext } from 'storyblok-js-client';
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { storeToRefs } from 'pinia';
import { Vue3Marquee } from 'vue3-marquee';
import useLinkReplacer from '~/composables/useLinkReplacer';
import ConsumerType from '~/components/body/ConsumerType.vue';
import LangCurrencySelector from '~/components/menu/LangCurrencySelector.vue';
import LocationSelector from '~/components/menu/LocationSelector.vue';
import { trackButtonClick } from '~/composables/useTracking';

const uiStore = useUiStore();
const userStore = useUserStore();
const { showSiteNotice } = storeToRefs(uiStore);
const { accountUrl } = useLinkReplacer();

const props = defineProps<{
  settings: StoryBlokSiteNoticeData;
}>();

const globalContentStore = useGlobalContentStore();
const { hideSiteMessage } = storeToRefs(globalContentStore);

userStore.loadNotifications();

const hasSiteUsps = computed(() => ((props.settings.siteUspList || []).length > 0));
const hasNotification = computed(() => userStore.loggedIn && !userStore.isSalesRepUser && userStore.getUnseenNotifications('orderProposal').length > 0);

// Free delivery days for logged-in SE B2B users (not samplesOnly)
const { $t } = useNuxtApp();
const showFreeDeliveryDays = computed(() => {
  const mod = userStore.userProfile?.company?.mod;
  return globalContentStore.getIsSeB2b && userStore.loggedIn && !userStore.isSamplesOnlyUser && /\d/.test(mod);
});

const freeDeliveryDaysList = computed(() => {
  const mod = userStore.userProfile?.company?.mod ?? '';
  const dayNumbers = [...mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
  return dayNumbers.map((num) => $t(`weekday.${num}`));
});

const shouldShowNotice = computed(() => {
  return !hideSiteMessage.value || hasSiteUsps || hasNotification || !uiStore.isMobile;
});
const siteMessageText = !hideSiteMessage.value ? computed(() => renderRichText(props.settings.siteMessage as ISbRichtext)) : false;
const readMoreContent = computed(() => renderRichText(props.settings.readMoreContent as ISbRichtext));
const wysiwygContent = ref<HTMLElement | null>(null);

const closeSiteMessage = () => {
  globalContentStore.setHideSiteMessage(true);
};

const openReadMoreModal = () => {
  uiStore.setShowSiteNotice(true);
};

const currentUspIndex = ref(0);
const totalUsps = (props.settings.siteUspList || []).length;

onMounted(() => {
  setInterval(() => {
    currentUspIndex.value = (currentUspIndex.value + 1) % totalUsps;
  }, 3000); // Change USP every 3 seconds (adjust as needed)
});

const duration = ref(props.settings.siteMarqueeSpeed ? parseInt(props.settings.siteMarqueeSpeed?.toString(), 10) : 10); // Default to 10 if not present

const bgAndTextColor = computed(()=> {
  if (props.settings.displaySiteMessage && !hideSiteMessage.value) {
  /*:class="settings.invertTextColorMessage ? 'text-lightest' : 'text-darkest'"
  :style="{backgroundColor : settings.siteMessageBackgroundColor?.color}"*/

    return {
      backgroundColor: props.settings.siteMessageBackgroundColor?.color,
      color: props.settings.invertTextColorMessage ? '--var(--color-text-inverse)' : '--var(--color-text-base)',
      '--siteNoticeBg': props.settings.siteMessageBackgroundColor?.color,
    };
  }

  if (props.settings.displaySiteMarquee && props.settings.siteMarqueeItemList.length > 0) {
    return {
      backgroundColor : props.settings.siteMarqueeBackgroundColor?.color,
      '--siteNoticeBg': props.settings.siteMarqueeBackgroundColor?.color,
    };
  }

  if (showFreeDeliveryDays.value || hasSiteUsps.value) {
    return {
      backgroundColor : props.settings.siteUspBackgroundColor?.color,
      '--siteNoticeBg': props.settings.siteUspBackgroundColor?.color,
    };
  }

  return {};

});

const effectiveInvertText = computed(() => {
  // Top layer: Site Message is enabled
  if (props.settings.displaySiteMessage && !hideSiteMessage.value) {
    return props.settings.invertTextColorMessage;
  }
  // Middle layer: Site Marquee is enabled (hiding Site USP)
  if (props.settings.displaySiteMarquee && props.settings.siteMarqueeItemList.length > 0) {
    return props.settings.invertTextColorMarquee;
  }
  // Bottom layer: Free delivery days or Site USP is active
  if (showFreeDeliveryDays.value || (props.settings.siteUspList || []).length > 0) {
    return props.settings.siteUspTextColor;
  }
  // Fallback default (if none are enabled)
  return false;
});

</script>

<style lang="postcss">
.truncate-p > p {
  @apply truncate text-10 desk:text-12 leading-single;
}
.usp-text {
  opacity: 0;
  transition: opacity 0.5s ease;
}

.usp-text.active {
  opacity: 1;
}
</style>
